// PricingComparison — Feature comparison table with scroll-triggered animations

"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCircleCheckOutline18 } from "nucleo-ui-outline-18";
import { IconMinus } from "nucleo-sharp";
import Image from "next/image";
import { motion } from "framer-motion";
import type { TierInfo } from "@/types/billing";
import type { ComparisonGroup } from "./pricing-data";
import { buildResourceComparisonRows, PRICING_TIERS, STATIC_COMPARISON_DATA, TIER_ICON_MAP } from "./pricing-data";
const TIER_COLUMNS = PRICING_TIERS.map(t => ({
  id: t.id,
  label: t.name,
  popular: t.popular
}));

/** Renders a cell value: true = circle-check icon, false = minus icon, string = text */
function CellValue(t0) {
  const $ = _c(4);
  const {
    value
  } = t0;
  if (value === true) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <IconCircleCheckOutline18 className="text-ds-focus-brand mx-auto h-5 w-5" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (value === false) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <IconMinus className="mx-auto h-5 w-5 text-zinc-300 dark:text-zinc-600" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== value) {
    t1 = <span>{value}</span>;
    $[2] = value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
interface PricingComparisonProps {
  backendTiers?: TierInfo[];
}
export function PricingComparison(t0) {
  const $ = _c(39);
  const {
    backendTiers
  } = t0;
  let T0;
  let t1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== backendTiers) {
    const groups = backendTiers ? [buildResourceComparisonRows(backendTiers), ...STATIC_COMPARISON_DATA] : STATIC_COMPARISON_DATA;
    T0 = motion.section;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        opacity: 0,
        y: 40
      };
      t6 = {
        opacity: 1,
        y: 0
      };
      t7 = {
        once: true,
        margin: "-100px"
      };
      t8 = {
        duration: 0.6
      };
      $[13] = t5;
      $[14] = t6;
      $[15] = t7;
      $[16] = t8;
    } else {
      t5 = $[13];
      t6 = $[14];
      t7 = $[15];
      t8 = $[16];
    }
    t9 = "py-16";
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <h2 className="text-ds-text-primary mb-2 text-center text-3xl font-medium tracking-tight md:text-4xl">Compare plans</h2>;
      t11 = <p className="text-ds-text-muted mb-8 text-center text-base">Choose the plan that fits your automation needs today — scale as you grow.</p>;
      $[17] = t10;
      $[18] = t11;
    } else {
      t10 = $[17];
      t11 = $[18];
    }
    t4 = "overflow-x-auto";
    t2 = "w-full border-collapse border-t border-zinc-200 text-sm";
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <thead><tr className="border-b border-zinc-200"><th className="text-ds-text-muted h-16 border-r border-zinc-200 px-6 text-left font-medium">Feature</th>{TIER_COLUMNS.map(_temp)}</tr></thead>;
      $[19] = t3;
    } else {
      t3 = $[19];
    }
    t1 = groups.map(_temp2);
    $[0] = backendTiers;
    $[1] = T0;
    $[2] = t1;
    $[3] = t10;
    $[4] = t11;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    T0 = $[1];
    t1 = $[2];
    t10 = $[3];
    t11 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t12;
  if ($[20] !== t1) {
    t12 = <tbody>{t1}</tbody>;
    $[20] = t1;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t2 || $[24] !== t3) {
    t13 = <table className={t2}>{t3}{t12}</table>;
    $[22] = t12;
    $[23] = t2;
    $[24] = t3;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t4) {
    t14 = <div className={t4}>{t13}</div>;
    $[26] = t13;
    $[27] = t4;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== T0 || $[30] !== t10 || $[31] !== t11 || $[32] !== t14 || $[33] !== t5 || $[34] !== t6 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t15 = <T0 initial={t5} whileInView={t6} viewport={t7} transition={t8} className={t9}>{t10}{t11}{t14}</T0>;
    $[29] = T0;
    $[30] = t10;
    $[31] = t11;
    $[32] = t14;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  return t15;
}

/** Renders one group: category header + feature rows */
function _temp2(group) {
  return <ComparisonGroupRows key={group.category} group={group} />;
}
function _temp(col, i) {
  return <th key={col.id} className={`h-16 px-6 text-center font-medium ${i < TIER_COLUMNS.length - 1 ? "border-r border-zinc-200" : ""}`}><div className="flex items-center justify-center gap-2"><Image src={TIER_ICON_MAP[col.id] ?? TIER_ICON_MAP.free} alt={`${col.label} plan icon`} width={16} height={16} className="size-4" /><span className="text-ds-text-secondary font-mono text-base font-medium uppercase tracking-[-0.32px]">{col.label}</span>{col.popular && <span className="border-ds-info-border bg-ds-info-surface text-ds-info-text rounded-full border px-2 py-0.5 text-xs font-medium">Popular</span>}</div></th>;
}
function ComparisonGroupRows(t0) {
  const $ = _c(7);
  const {
    group
  } = t0;
  let t1;
  if ($[0] !== group.category) {
    t1 = <tr className="border-b border-zinc-200"><th colSpan={TIER_COLUMNS.length + 1} scope="colgroup" className="text-ds-focus-brand h-16 px-6 text-left font-mono text-sm font-medium uppercase">{group.category}</th></tr>;
    $[0] = group.category;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== group.rows) {
    t2 = group.rows.map(_temp3);
    $[2] = group.rows;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp3(row, i) {
  return <motion.tr key={row.feature} initial={{
    opacity: 0,
    x: -20
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.3,
    delay: i * 0.05
  }} className="border-b border-zinc-200"><td className="text-ds-text-secondary h-16 border-r border-zinc-200 px-6 font-medium">{row.feature}</td><td className="h-16 border-r border-zinc-200 px-6 text-center text-zinc-600"><CellValue value={row.free} /></td><td className="h-16 border-r border-zinc-200 px-6 text-center text-zinc-600"><CellValue value={row.starter} /></td><td className="h-16 border-r border-zinc-200 px-6 text-center text-zinc-600"><CellValue value={row.pro} /></td><td className="h-16 border-r border-zinc-200 px-6 text-center text-zinc-600"><CellValue value={row.biz} /></td><td className="h-16 px-6 text-center text-zinc-600"><CellValue value={row.enterprise} /></td></motion.tr>;
}